import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import Request from 'utilities/Request.jsx';
import MealType from 'models/MealType.jsx';
import mealName from './mealName';

/** How long typing has to pause before the search is sent. */
export const SEARCH_DELAY = 250;

/** "Today", "3 days ago", "in 2 weeks": how far a meal's day is from today, counted in days. */
export function whenFromToday(date, today = moment()) {
	const days = date.clone().startOf('day').diff(today.clone().startOf('day'), 'days');
	if(days === 0) {
		return 'Today';
	}
	if(days === -1) {
		return 'Yesterday';
	}
	if(days === 1) {
		return 'Tomorrow';
	}
	return date.clone().startOf('day').from(today.clone().startOf('day'));
}

const slotName = (type) => {
	const id = MealType.normalize(type);
	const slot = MealType.slots().find((candidate) => candidate.id === id);
	return slot ? slot.name : MealType.other().name;
};

const dateOf = (meal) => moment({ year: meal.year, month: meal.month - 1, day: meal.day });

/**
 * A search of every meal the budget has put on the calendar, by its name or its meal idea's name,
 * for "when did we last have Bibimbap?". The matches come newest first, each with its day, slot
 * and how long ago that was, and picking one calls `onPick` with its day.
 *
 * `mealIdeasById` names a meal the way the calendar does (mealName()). `delay` is how long typing
 * has to pause before the search goes out.
 */
export default function MealSearch({ mealIdeasById = null, onPick, delay = SEARCH_DELAY }) {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [open, setOpen] = useState(false);
	const root = useRef(null);
	// Only the newest request's answer is shown; an older one arriving late is dropped.
	const latest = useRef(0);

	useEffect(() => {
		const trimmed = query.trim();
		const request = ++latest.current;
		if(!trimmed) {
			setResults(null);
			setLoading(false);
			setFailed(false);
			return undefined;
		}
		setLoading(true);
		const timer = setTimeout(() => {
			Request.apiv1Get('budget/search_meals', { query: trimmed })
				.then((response) => {
					if(request === latest.current) {
						setResults({ meals: response.meals || [], hasMore: !!response.has_more });
						setFailed(false);
						setLoading(false);
					}
				})
				.catch(() => {
					if(request === latest.current) {
						setResults(null);
						setFailed(true);
						setLoading(false);
					}
				});
		}, delay);
		return () => clearTimeout(timer);
	}, [query, delay]);

	useEffect(() => {
		if(!open) {
			return undefined;
		}
		const close = (event) => {
			if(root.current && !root.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', close);
		document.addEventListener('touchstart', close);
		return () => {
			document.removeEventListener('mousedown', close);
			document.removeEventListener('touchstart', close);
		};
	}, [open]);

	const keyDown = (event) => {
		if(event.key === 'Escape') {
			if(query) {
				setQuery('');
			} else {
				event.target.blur();
				setOpen(false);
			}
		}
	};

	const pick = (meal) => {
		setOpen(false);
		onPick(dateOf(meal));
	};

	const today = moment();
	const showPanel = open && query.trim() !== '';

	return (
		<div className='meal-search' ref={root}>
			<i className='material-icons meal-search__icon' aria-hidden='true'>search</i>
			<input
				type='search'
				className='meal-search__input browser-default'
				placeholder='Search meals'
				aria-label='Search meals'
				value={query}
				onChange={(event) => {
					setQuery(event.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onKeyDown={keyDown}
			/>
			{showPanel &&
			<div className='meal-search__panel'>
				{failed &&
				<div className='meal-search__status'>The search failed. Try again.</div>
				}
				{!failed && !results && loading &&
				<div className='meal-search__status'>Searching…</div>
				}
				{!failed && results && results.meals.length === 0 &&
				<div className='meal-search__status'>No meals match “{query.trim()}”.</div>
				}
				{!failed && results && results.meals.length > 0 &&
				<React.Fragment>
					<div className='meal-search__status'>
						{results.hasMore ? `The latest ${results.meals.length} matches` :
							results.meals.length === 1 ? '1 match' : `${results.meals.length} matches`}
					</div>
					<ul className='meal-search__results'>
						{results.meals.map((meal) => {
							const date = dateOf(meal);
							const idea = mealIdeasById && meal.meal_idea_id ? mealIdeasById[meal.meal_idea_id] : null;
							return (
								<li key={meal.id}>
									<button type='button' className='btn-flat meal-search__result' onClick={() => pick(meal)}>
										<span className='meal-search__name'>{mealName(meal, idea) || '(unnamed)'}</span>
										<span className='meal-search__when'>
											{date.format('ddd, MMM D, YYYY')} · {slotName(meal.type)}
										</span>
										<span className='meal-search__ago'>{whenFromToday(date, today)}</span>
									</button>
								</li>
							);
						})}
					</ul>
				</React.Fragment>
				}
			</div>
			}
		</div>
	);
}
